import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { not } from 'ember-truth-helpers';
import PrinterSelector from 'garaje/components/printer-selector';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import type Entry from 'garaje/models/entry';
import type Invite from 'garaje/models/invite';
import type Printer from 'garaje/models/printer';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import { isPrinterReachable } from 'garaje/utils/printer-reachability';

interface ReprintBadgeModalSignature<T extends Invite | Entry> {
  Args: {
    /**
     * either an invite or an entry
     */
    model: T;
    /**
     * available printers
     */
    printers: Printer[];
    /**
     * method to send the entry and printer
     */
    reprintBadge: (model: T, printer?: Printer | null) => void;
    /**
     * set to true to display modal
     */
    displayModal: (show: boolean) => void;
  };
  Element: HTMLDivElement;
}

export default class ReprintBadgeModal<T extends Invite | Entry> extends Component<ReprintBadgeModalSignature<T>> {
  @service declare featureFlags: FeatureFlagsService;

  @tracked selectedPrinter?: Printer;

  isPrinterDisabled = (printer: Printer): boolean => {
    if (this.featureFlags.isEnabled('visitors-printer-failover')) {
      return !isPrinterReachable(printer, this.args.printers);
    }
    return printer.status !== 'connected';
  };

  closeModal = (): void => {
    this.selectedPrinter = undefined;
    this.args.displayModal(false);
  };

  selectPrinter = (printer?: Printer): void => {
    this.selectedPrinter = printer;
  };

  reprintBadge = (): void => {
    const { reprintBadge, model } = this.args;
    reprintBadge(model, this.selectedPrinter);
    this.closeModal();
  };

  <template>
    <ModalDialog @clickOutsideToClose={{false}} @targetAttachment="center" @translucentOverlay={{true}}>
      <div class="flow-root m-2">
        <button
          data-test-close-modal
          {{on "click" this.closeModal}}
          class="btn btn--small btn--outline btn--cancel float-right mr-2 mt-2"
        >
          &times;
        </button>
      </div>
      <div class="px-8 pb-8" data-test-select-printer-modal>
        <h1 class="mb-4">
          Select Printer
        </h1>
        <div data-test-printers-list>
          <PrinterSelector
            data-test-reprint-badge-printer-selector
            @selectedPrinter={{this.selectedPrinter}}
            @printers={{@printers}}
            @onChange={{this.selectPrinter}}
            @isDisabled={{this.isPrinterDisabled}}
          />
        </div>
        <div class="pt-4 flow-root">
          <button
            data-test-print-badge
            class="btn float-right ml-4 btn--small btn--brand"
            disabled={{not this.selectedPrinter}}
            {{on "click" this.reprintBadge}}
          >
            Print badge
          </button>
        </div>
      </div>
    </ModalDialog>
  </template>
}
